import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import { formatUzs, formatNumber } from '../lib/format';
import { BarChart, LineChart, PieChart } from '../components/Chart';
import { format, parseISO } from 'date-fns';

type Range = 7 | 14 | 30 | 90;

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: 7, label: '7 kun' },
  { value: 14, label: '14 kun' },
  { value: 30, label: '30 kun' },
  { value: 90, label: '90 kun' },
];

const CATEGORY_COLORS: Record<string, string> = {
  DIAGNOSTIC: '#06b6d4',
  OIL_CHANGE: '#f59e0b',
  BRAKES: '#ef4444',
  ENGINE: '#dc2626',
  TRANSMISSION: '#7c3aed',
  ELECTRICAL: '#3b82f6',
  TIRES: '#10b981',
  BODY: '#ec4899',
  WASH: '#14b8a6',
  OTHER: '#94a3b8',
};

export function StatsPage() {
  const auth = useAuth();
  const [range, setRange] = useState<Range>(14);

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ['stats-overview', auth.activeWorkshop?.id, range],
    queryFn: () => api.overview(range),
    enabled: !!auth.activeWorkshop,
  });

  const { data: revenue, isLoading: loadingRevenue } = useQuery({
    queryKey: ['stats-revenue', auth.activeWorkshop?.id, range],
    queryFn: () => api.revenue(range),
    enabled: !!auth.activeWorkshop,
  });

  const { data: topServices, isLoading: loadingTop } = useQuery({
    queryKey: ['stats-top-services', auth.activeWorkshop?.id, range],
    queryFn: () => api.topServices(range),
    enabled: !!auth.activeWorkshop,
  });

  const { data: peakHours, isLoading: loadingPeak } = useQuery({
    queryKey: ['stats-peak-hours', auth.activeWorkshop?.id],
    queryFn: () => api.peakHours(),
    enabled: !!auth.activeWorkshop,
  });

  const { data: customers, isLoading: loadingCustomers } = useQuery({
    queryKey: ['stats-customers', auth.activeWorkshop?.id, range],
    queryFn: () => api.customers(range),
    enabled: !!auth.activeWorkshop,
  });

  const isLoading = loadingOverview || loadingRevenue || loadingTop || loadingPeak || loadingCustomers;

  if (isLoading && !overview) return <div className="spinner" />;
  if (!overview) return null;

  const revenueData = (revenue ?? []).map((r) => ({
    label: format(parseISO(r.date), 'dd'),
    value: r.paid,
  }));
  const ordersData = (revenue ?? []).map((r) => ({
    label: format(parseISO(r.date), 'dd'),
    value: r.orders,
  }));

  const topServiceData = (topServices?.services ?? []).slice(0, 8).map((s: any) => ({
    label: s.name.length > 10 ? s.name.slice(0, 10) + '…' : s.name,
    value: s.count,
    color: CATEGORY_COLORS[s.category] ?? 'var(--primary)',
  }));

  const logTypeData = (() => {
    const types = topServices?.logTypes ?? {};
    return [
      { label: 'Ish', value: types.WORK ?? 0, color: 'var(--info)' },
      { label: 'Ehtiyot qism', value: types.PART ?? 0, color: 'var(--warning)' },
      { label: 'Izoh', value: types.NOTE ?? 0, color: 'var(--text-dim)' },
    ];
  })();

  const peakData = (peakHours ?? []).map((p) => ({
    label: `${String(p.hour).padStart(2, '0')}:00`,
    value: p.scheduled,
  }));

  return (
    <>
      <div className="row-between" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>📊 Tahlil</h3>
        <div className="tab-bar" style={{ margin: 0, padding: 3 }}>
          {RANGE_OPTIONS.map((r) => (
            <div
              key={r.value}
              className={`tab ${range === r.value ? 'active' : ''}`}
              onClick={() => setRange(r.value)}
              style={{ padding: '4px 10px', fontSize: 12 }}
            >
              {r.label}
            </div>
          ))}
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card received">
          <div className="stat-label">Buyurtmalar</div>
          <div className="stat-value">{formatNumber(overview.orders)}</div>
        </div>
        <div className="stat-card revenue">
          <div className="stat-label">Tushum</div>
          <div className="stat-value amount" style={{ fontSize: 18 }}>{formatUzs(overview.revenue)}</div>
        </div>
        <div className="stat-card inProgress">
          <div className="stat-label">O'rtacha chek</div>
          <div className="stat-value amount" style={{ fontSize: 18 }}>{formatUzs(overview.avgTicket)}</div>
        </div>
        <div className={`stat-card ${overview.growth >= 0 ? 'success' : 'danger'}`}>
          <div className="stat-label">O'sish</div>
          <div className="stat-value">{overview.growth >= 0 ? '+' : ''}{overview.growth}%</div>
          <div className="stat-sub">oldingi davrga nisbatan</div>
        </div>
        <div className="stat-card ready">
          <div className="stat-label">Faol mijozlar</div>
          <div className="stat-value">{formatNumber(overview.activeCustomers)}</div>
        </div>
        <div className="stat-card delivered">
          <div className="stat-label">Tugatish %</div>
          <div className="stat-value">{overview.completionRate}%</div>
        </div>
      </div>

      <div className="card">
        <div className="row-between" style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600 }}>💵 Tushum dinamikasi</div>
          <div className="muted" style={{ fontSize: 12 }}>To'langan summa</div>
        </div>
        <LineChart data={revenueData} color="var(--success)" formatValue={(n) => formatUzs(n)} />
      </div>

      <div className="card">
        <div className="row-between" style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600 }}>📋 Buyurtmalar soni</div>
          <div className="muted" style={{ fontSize: 12 }}>Har kuni</div>
        </div>
        <BarChart data={ordersData} color="var(--primary)" formatValue={(n) => formatNumber(n)} />
      </div>

      <div className="card">
        <div className="row-between" style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600 }}>🏆 Top xizmatlar</div>
          <div className="muted" style={{ fontSize: 12 }}>Bronlar bo'yicha</div>
        </div>
        {topServiceData.length > 0 ? (
          <BarChart data={topServiceData} formatValue={(n) => `${n} ta`} />
        ) : (
          <div className="empty" style={{ padding: 20, fontSize: 13 }}>Hali xizmatlar yo'q</div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 10 }}>📝 Log turlari</div>
          <PieChart segments={logTypeData} />
        </div>
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 10 }}>⏰ Band vaqtlari</div>
          <BarChart data={peakData} height={100} formatValue={(n) => `${n}`} />
          <div className="muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 4 }}>
            Eng ko'p band: {peakData.reduce((max, p) => (p.value > max.value ? p : max), peakData[0] ?? { value: 0, label: '—' }).label}
          </div>
        </div>
      </div>

      {customers && (
        <div className="card">
          <div className="row-between" style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 600 }}>👥 Mijozlar</div>
            <div className="muted" style={{ fontSize: 12 }}>Jami: {formatNumber(customers.total)}</div>
          </div>
          <div className="row" style={{ gap: 12, marginBottom: 10 }}>
            <span className="tag success">Yangi: {customers.new}</span>
            <span className="tag primary">Qaytuvchi: {customers.returning}</span>
          </div>
          {customers.topSpenders && customers.topSpenders.length > 0 && (
            <>
              <div className="field-label" style={{ marginBottom: 6, fontSize: 11 }}>Eng ko'p xarajat qilganlar</div>
              {customers.topSpenders.slice(0, 5).map((c: any, i: number) => (
                <div key={c.id} className="row-between" style={{ padding: '5px 0', borderBottom: i < 4 ? '1px solid var(--border)' : undefined }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{i + 1}. {c.name}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{c.phone} · {c.orders} ta buyurtma</div>
                  </div>
                  <div className="amount" style={{ fontWeight: 600 }}>{formatUzs(c.total)}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}
